import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { getPlanName } from './plans';
import { SubsContext } from './SubscriptionInfo';

const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
];

const FormContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
`;

const FormSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #E5E7EB;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #374151;
  font-size: 0.95rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  background-color: #F9FAFB;

  &:focus {
    outline: none;
    border-color: rgba(0, 0, 0, 0.05);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    background-color: white;
  }

  &::placeholder {
    color: #9CA3AF;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  background-color: #F9FAFB;
  appearance: auto;

  &:focus {
    outline: none;
    border-color: rgba(0, 0, 0, 0.05);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    background-color: white;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 20px;
  text-align: center;
`;

const PageTitleWrapper = styled.div`
  background: linear-gradient(135deg, #ff7c52 0%, #ffb347 100%);
  padding: 32px;
  border-radius: 12px;
  margin-bottom: 32px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const PageTitle = styled.h1`
  color: black;
  font-weight: 600;
  text-align: center;
  font-size: 28px;
  margin: 0;
`;



const ErrorMessage = styled.div`
  color: #DC2626;
  margin-top: 8px;
  font-size: 0.875rem;
  display: ${props => props.visible ? 'block' : 'none'};
`;

const convertTime = (ms) => {
  if (ms < 0) {
    return 'soon';
  }
  let remainingMs = ms;
  const days = Math.floor(remainingMs / (24 * 60 * 60 * 1000));
  remainingMs %= 24 * 60 * 60 * 1000;
  const hours = Math.floor(remainingMs / (60 * 60 * 1000));
  remainingMs %= 60 * 60 * 1000;
  const minutes = Math.floor(remainingMs / (60 * 1000));
  remainingMs %= 60 * 1000;
  const seconds = Math.floor(remainingMs / 1000);

  let daysLeft = '';
  if (days) {
    if (days === 1) {
      daysLeft = '1 day, '
    } else {
      daysLeft = `${days} days, `;
    }
  }
  return `in ${daysLeft}${hours < 10 ? `0${hours}` : hours}:${minutes < 10 ? `0${minutes}` : minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
}

const CustomerForm = ({ customerInfo, onInputChange, error, addressError }) => {
  const { subscription, plans, subscriptionLevel, billsbyCustomerId, previousPlan, freeTrialPeriod } = useContext(SubsContext);
  const nextBillingTime = subscription ? Date.parse(subscription.nextBillingDate) : '';
  const getTimeRemaining = () => subscription ? nextBillingTime - Date.now() - new Date().getTimezoneOffset() * 60 * 1000 : 0;

  const [timeRemaining, setTimeRemaining] = useState(getTimeRemaining())
  const currSubscriptionLevel = subscription ? subscription.planName : subscriptionLevel;
  const nextSubscriptionLevel = subscription && subscription.planIdNext ? getPlanName(subscription.planIdNext, plans) : currSubscriptionLevel;

  if (subscription) {
    setInterval(() => {
      setTimeRemaining(getTimeRemaining())
    }, 2000);
  }

  const isSubscriptionCancelled = () => {
    if (!subscription) {
      return false;
    }
    return !!previousPlan;
  }

  return (
    <FormContainer>
      {subscription && (
        <div>
          <PageTitleWrapper>
            <PageTitle>Subscription Level: {currSubscriptionLevel}</PageTitle>
          </PageTitleWrapper>
          <SectionTitle>
            {subscription.status === 'InTrial' && (<div>Free Trial</div>)}
            {subscription.status === 'Cancelled' && (<div>Cancelled</div>)}
            {subscription.status === 'Suspended' && (<div>Suspended from {nextSubscriptionLevel} (you may resubscribe by selecting a plan below)</div>)}
            {subscription.status !== 'Cancelled' && subscription.status !== 'Suspended' && (
              <span>
                {currSubscriptionLevel !== nextSubscriptionLevel && (
                  <div>
                    {isSubscriptionCancelled() && (<span>Your subscription will be cancelled on {subscription.nextBillingDate.slice(0, 10)} ({convertTime(timeRemaining)})</span>)}
                    {!isSubscriptionCancelled() && (<span>Your {subscription.status === 'InTrial' ? `subscription level ${subscriptionLevel !== nextSubscriptionLevel ? `of ${nextSubscriptionLevel}` : ''} starts ` : `new subscription level of ${nextSubscriptionLevel} starts `}
                      on {subscription.nextBillingDate.slice(0, 10)} ({convertTime(timeRemaining)}) at {subscription.nextCycleRecurringBillingAmount}</span>)}
                  </div>
                )}
                {currSubscriptionLevel === nextSubscriptionLevel && (
                  <div>
                    Your {subscription.status === 'InTrial' ? `first billing for ${nextSubscriptionLevel}` : 'next billing'} is on&nbsp;
                    {subscription.nextBillingDate.slice(0, 10)} ({convertTime(timeRemaining)}) at {subscription.nextCycleRecurringBillingAmount}
                    <div style={{ marginTop: '10px', fontSize: '1rem', fontWeight: 'normal' }}>
                      Your access to ICON includes a FREE two-week trial period at the <i>Subscription level</i> of ICON PRO from the date you login /or register.
                      At the end of that trial period you will be required to choose a subscription level.
                    </div>
                  </div>
                )}
              </span>
            )}
          </SectionTitle>
        </div>
      )}
      {!subscription && (
        <PageTitle>Subscription {subscriptionLevel ? 'Level: ' : 'Plans'}{subscriptionLevel}</PageTitle>
      )}
      {!subscription && billsbyCustomerId && (
        <center>
          Subscription cannot be updated at this time.  Try again later.
        </center>
      )}
      {!subscription && subscriptionLevel === 'New' && (
        <p>
          Please select from one of the plans below.  You must supply a credit card, even if
          you can get ICON Core for free by virtue of an Industrial Affiliation.  Your {freeTrialPeriod} <b>Free Trial</b> period starts
          after your credit card is accepted.  During the free trial Period the subscription level will be <b>ICON Pro</b>, which will
          allow you to take advantage of all the features ICON has to offer.  After the free trial ends, you will then have whatever
          level you select below.  You may change that selection at anytime during the trial period.
        </p>
      )}
      {subscription && subscription.status === 'InTrial' && (
        <p>
          During the <b>Free Trial</b> period, your Subscription Level is <b>ICON Pro</b>,
          but whatever subscription level you select below (currently <b>{nextSubscriptionLevel}</b>), will take effect when the free trial period ends.
        </p>
      )}
      {subscription && subscription.status === 'Active' && (
        <center>
          {isSubscriptionCancelled() && (<span> You may undo the cancellation by clicking on <b>Reinstate Subscription</b> or by selecting a new subscription level.</span>)}
          {!isSubscriptionCancelled() && (<span> If you switch to a less expensive subscription level, the switch will occur at the end of the current billing cycle.<br />
            If you switch to a more expensive subscription level, the switch will occur immediately.</span>)}
        </center>
      )}
      {!(!subscription && billsbyCustomerId) && (
        <div>
          <FormSection>
            <SectionTitle>Company Information</SectionTitle>
            <FormRow>
              <FormGroup>
                <Label>Company Name</Label>
                <Input
                  type="text"
                  name="companyName"
                  value={customerInfo.companyName}
                  onChange={onInputChange}
                  placeholder="Enter company name"
                  required
                  disabled
                />
              </FormGroup>
              <FormGroup>
                <Label>Email</Label>
                <Input
                  type="email"
                  name="email"
                  value={customerInfo.email}
                  onChange={onInputChange}
                  placeholder="company@example.com"
                  required
                  disabled
                />
              </FormGroup>
            </FormRow>
            <FormRow>
              <FormGroup>
                <Label>First Name</Label>
                <Input
                  type="text"
                  name="firstName"
                  value={customerInfo.firstName}
                  onChange={onInputChange}
                  placeholder="Enter first name"
                  required
                  disabled
                />
              </FormGroup>
              <FormGroup>
                <Label>Last Name</Label>
                <Input
                  type="text"
                  name="lastName"
                  value={customerInfo.lastName}
                  onChange={onInputChange}
                  placeholder="Enter last name"
                  required
                  disabled
                />
              </FormGroup>
            </FormRow>
          </FormSection>

          <FormSection>
            <SectionTitle>Credit Card Billing Address</SectionTitle>
            <FormGroup>
              <Label>Street Address</Label>
              <Input
                type="text"
                name="addressLine1"
                value={customerInfo.addressLine1}
                onChange={onInputChange}
                placeholder="Enter street address"
                required
              />
              <ErrorMessage visible={addressError.addressLine1}>{addressError.addressLine1}</ErrorMessage>
            </FormGroup>
            <FormRow>
              <FormGroup>
                <Label>City</Label>
                <Input
                  type="text"
                  name="city"
                  value={customerInfo.city}
                  onChange={onInputChange}
                  placeholder="Enter city"
                  required
                />
                <ErrorMessage visible={addressError.city}>{addressError.city}</ErrorMessage>
              </FormGroup>
              <FormGroup>
                <Label>State</Label>
                <Select
                  name="state"
                  value={customerInfo.state}
                  onChange={onInputChange}
                  required
                >
                  <option value="">Select a state</option>
                  {US_STATES.map(state => (
                    <option key={state.value} value={state.value}>
                      {state.label}
                    </option>
                  ))}
                </Select>
                <ErrorMessage visible={addressError.state}>{addressError.state}</ErrorMessage>
              </FormGroup>
            </FormRow>
            <FormRow>
              <FormGroup>
                <Label>ZIP Code</Label>
                <Input
                  type="text"
                  name="postCode"
                  value={customerInfo.postCode}
                  onChange={onInputChange}
                  placeholder="Enter ZIP code"
                  required
                />
                <ErrorMessage visible={addressError.postCode}>{addressError.postCode}</ErrorMessage>
              </FormGroup>
              <FormGroup>
                <Label>Country</Label>
                <Input
                  type="text"
                  name="country"
                  value="United States"
                  disabled
                  style={{ backgroundColor: '#F3F4F6' }}
                />
                <ErrorMessage visible={addressError.country}>{addressError.country}</ErrorMessage>
              </FormGroup>
            </FormRow>
          </FormSection>
        </div>
      )}
      <ErrorMessage visible={!!error}>{error}</ErrorMessage>
    </FormContainer>
  );
}

CustomerForm.propTypes = {
  customerInfo: PropTypes.shape({
    email: PropTypes.string.isRequired,
    companyName: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    addressLine1: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    postCode: PropTypes.string.isRequired,
    industryAssociations: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  addressError: PropTypes.shape({
    addressLine1: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    postCode: PropTypes.string,
    country: PropTypes.string,
  }).isRequired,
  onInputChange: PropTypes.func.isRequired,
  error: PropTypes.string,
};

CustomerForm.defaultProps = {
  error: ''
};

export default CustomerForm;



// WEBPACK FOOTER //
// ./src/components/Billsby/CustomerForm.jsx